import '../../styles/guide.css';
import { Animated, Box, Packet, Tag, Wire, useSequence, type Phase } from '../blog/Sequence';

/**
 * One self-playing figure for each long guide page (/mcp-permissions,
 * /mcp-agent-identity, /mcp-audit-logs). Built on the launch post's Sequence
 * primitives, so they look and behave like the blog's figures: they play when
 * scrolled into view, pause off screen, hold still under reduced motion, and
 * the prerendered HTML is the final frame.
 *
 * Product behaviour matches the page copy around each figure: the 403 text is
 * the app's, a tool-level deny overrides a server grant, and an unanswerable
 * check with no recent decision refuses. Agent, person and server names are
 * illustrative and every caption says so.
 */

const DENY_MSG = (tool: string) => `You don't have permission to call tool '${tool}'`;

// ─── 1. /mcp-permissions: what decides each call ────────────────────────────

const PERM_TOOLS = ['list_issues', 'issue_read', 'add_issue_comment', 'merge_pull_request'] as const;

const PERM_PHASES: Phase[] = [
  { act: 'Rules', label: 'grant', beats: 4 },
  { act: 'Rules', label: 'deny', beats: 4 },
  { act: 'Permission service down', label: 'recent', beats: 4 },
  { act: 'Permission service down', label: 'unknown', beats: 4 },
];
/** Which tool row each phase calls, and whether the gateway refuses it. */
const PERM_CALL = [0, 3, 1, 2];
const PERM_REFUSED = [false, true, false, true];

/**
 * Two drawings of the same scene: wide for desktop, tall for phones, where the
 * wide one would shrink to unreadable. CSS (guide.css) shows one of them.
 * `stop` is where the request waits at each beat when it is not at a tool.
 */
interface PermLayout {
  view: string;
  agent: { x: number; y: number };
  wire: [number, number, number, number];
  gate: { x: number; y: number; w: number; h: number };
  svcWire: [number, number, number, number];
  svc: { x: number; y: number; w: number; h: number; label: string; sub?: string };
  down: { x: number; y: number };
  panel: { x: number; y: number; w: number; h: number };
  row: (r: number) => { x: number; y: number; w: number };
  toolWire: (r: number) => [number, number, number, number];
  out: { x: number; y: number };
  gateStop: { x: number; y: number };
  atTool: (r: number) => { x: number; y: number };
}

const WIDE: PermLayout = {
  view: '0 0 900 300',
  agent: { x: 20, y: 115 },
  wire: [170, 150, 300, 150],
  gate: { x: 300, y: 90, w: 210, h: 120 },
  svcWire: [405, 210, 405, 236],
  svc: { x: 300, y: 236, w: 210, h: 50, label: 'permission service' },
  down: { x: 470, y: 261 },
  panel: { x: 620, y: 6, w: 270, h: 288 },
  row: (r) => ({ x: 636, y: 52 + r * 52, w: 238 }),
  toolWire: (r) => [510, 150, 636, 72 + r * 52],
  out: { x: 235, y: 150 },
  gateStop: { x: 405, y: 186 },
  atTool: (r) => ({ x: 560, y: 150 + (72 + r * 52 - 150) * 0.4 }),
};

const TALL: PermLayout = {
  view: '0 0 360 520',
  agent: { x: 40, y: 0 },
  wire: [115, 70, 115, 112],
  gate: { x: 20, y: 112, w: 190, h: 100 },
  svcWire: [210, 162, 226, 162],
  svc: { x: 226, y: 132, w: 124, h: 60, label: 'permissions', sub: 'service' },
  down: { x: 320, y: 120 },
  panel: { x: 0, y: 250, w: 360, h: 268 },
  row: (r) => ({ x: 12, y: 290 + r * 54, w: 336 }),
  toolWire: () => [115, 212, 115, 250],
  out: { x: 115, y: 91 },
  gateStop: { x: 115, y: 190 },
  atTool: () => ({ x: 115, y: 231 }),
};

export function PermissionCheck() {
  const seq = useSequence(PERM_PHASES);
  const p = seq.phase;
  const b = seq.beat;
  const row = PERM_CALL[p];
  const tool = PERM_TOOLS[row];
  const refused = PERM_REFUSED[p];
  const outage = p >= 2;

  const pkTone = b < 2 ? 'clay' : refused ? 'no' : b === 3 ? 'ok' : 'clay';
  const pkLabel = b < 2 ? tool : refused ? '403' : b === 3 ? '200' : tool;
  const gwTone = refused && b >= 2 ? 'no' : outage && b >= 1 ? 'hold' : 'clay';

  const draw = (L: PermLayout, className: string) => {
    const at = [L.out, L.gateStop, refused ? L.gateStop : L.atTool(row), L.out][b];
    return (
      <svg
        className={className}
        viewBox={L.view}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Animated diagram: an agent calls four tools on the GitHub MCP server through Statio. A server grant lets list_issues through, a tool deny refuses merge_pull_request with a 403, and while the permission service is down a recently decided call goes through and a never-decided call is refused."
      >
        <Box x={L.agent.x} y={L.agent.y} w={150} h={70} label="triage-bot" sub="agent" on={b === 0 || b === 3} />
        <Wire x1={L.wire[0]} y1={L.wire[1]} x2={L.wire[2]} y2={L.wire[3]} tone={b === 0 || b === 3 ? 'ink' : 'dim'} />
        <Box
          {...L.gate}
          kicker="gateway"
          label="Statio"
          sub={outage ? 'last decisions, cached' : 'checks every call'}
          tone={gwTone}
          on={b === 1 || b === 2}
        />

        <Wire
          x1={L.svcWire[0]}
          y1={L.svcWire[1]}
          x2={L.svcWire[2]}
          y2={L.svcWire[3]}
          tone={outage ? 'no' : 'dim'}
          dashed={outage}
        />
        <Box {...L.svc} tone={outage ? 'no' : 'ink'} />
        <Tag x={L.down.x} y={L.down.y} label="down" tone="no" show={outage} />

        <g transform={`translate(${L.panel.x} ${L.panel.y})`}>
          <rect width={L.panel.w} height={L.panel.h} rx="4" fill="var(--surface)" stroke="var(--rule)" />
          <text className="bq-cap" x="14" y="24">
            github mcp server
          </text>
        </g>
        {PERM_TOOLS.map((t, r) => {
          const denied = t === 'merge_pull_request';
          const hit = r === row && b >= 2 && !refused;
          const R = L.row(r);
          const w = L.toolWire(r);
          return (
            <g key={t}>
              {(L === WIDE || r === 0) && (
                <Wire
                  x1={w[0]}
                  y1={w[1]}
                  x2={w[2]}
                  y2={w[3]}
                  tone={hit || (L === TALL && b >= 2 && !refused) ? 'clay' : 'dim'}
                  dashed={L === WIDE && denied}
                />
              )}
              <g className={`bq-box${hit ? ' t-ok on' : ''}`} transform={`translate(${R.x} ${R.y})`}>
                <rect width={R.w} height="40" rx="3" />
                <text className="bq-lab" x="12" y="20" dominantBaseline="central" style={{ fontSize: 12 }}>
                  {t}
                </text>
              </g>
              <Tag x={R.x + R.w - 38} y={R.y + 20} label={denied ? 'denied' : 'granted'} tone={denied ? 'no' : 'ok'} />
            </g>
          );
        })}

        <Packet x={at.x} y={at.y} label={pkLabel} tone={pkTone} />
      </svg>
    );
  };

  const narrate = (phase: number, beat: number) =>
    [
      <>
        triage-bot is granted the <b>github</b> server, which covers every tool on it. It calls <code>list_issues</code>
        {beat >= 3 ? ' and gets the open issues back.' : '.'}
      </>,
      <>
        It calls <code>merge_pull_request</code>, which is denied for triage-bot.
        {beat >= 2 && (
          <>
            {' '}
            A tool deny beats the server grant, so the gateway answers <b>403</b>: &ldquo;
            {DENY_MSG('merge_pull_request')}&rdquo;. GitHub never sees the call.
          </>
        )}
      </>,
      <>
        The permission service is unreachable.
        {beat >= 1 && (
          <>
            {' '}
            The gateway decided <code>issue_read</code> for triage-bot a moment ago, so it uses that decision
          </>
        )}
        {beat >= 3 ? <> and the call goes through. An outage never revokes access mid-task.</> : beat >= 1 ? '.' : ''}
      </>,
      <>
        Still down, and triage-bot calls <code>add_issue_comment</code> for the first time.
        {beat >= 2 && (
          <>
            {' '}
            There is no recent decision to rely on, so the gateway refuses it with a <b>403</b>.
          </>
        )}
      </>,
    ][phase];

  return (
    <Animated
      title={
        <>
          triage-bot <span className="bw-note">calls tools on</span> github
        </>
      }
      seq={seq}
      phases={PERM_PHASES}
      narrate={narrate}
    >
      {draw(WIDE, 'gf-wide')}
      {draw(TALL, 'gf-tall')}
    </Animated>
  );
}

// ─── 2. /mcp-agent-identity: one key, or one identity each ──────────────────

const ID_PHASES: Phase[] = [
  { act: 'On a person’s key', label: 'working', beats: 2 },
  { act: 'On a person’s key', label: 'revoke', beats: 3 },
  { act: 'Their own identity', label: 'working', beats: 2 },
  { act: 'Their own identity', label: 'suspend', beats: 3 },
];

const CALLERS = [
  { name: 'dana@acme', kind: 'person' },
  { name: 'triage-bot', kind: 'agent' },
  { name: 'deploy-bot', kind: 'agent' },
] as const;

export function AgentIdentityLanes() {
  const seq = useSequence(ID_PHASES, { beatMs: 1100 });
  const p = seq.phase;
  const b = seq.beat;
  const own = p >= 2;
  // Who is cut off: revoking Dana's key stops all three; suspending the
  // agent stops only the agent.
  const stopped = (i: number) => (p === 1 && b >= 1) || (p === 3 && b >= 1 && i === 1);

  const narrate = (phase: number, beat: number) =>
    [
      <>
        Both agents run on <b>dana@acme</b>&rsquo;s token. Every call they make is logged as Dana, and each can do
        anything Dana can.
      </>,
      <>
        triage-bot misbehaves.
        {beat >= 1 ? (
          <>
            {' '}
            The only switch is Dana&rsquo;s token, so revoking it stops <b>triage-bot</b>, <b>deploy-bot</b> and{' '}
            <b>Dana</b>.
          </>
        ) : (
          ' The only way to stop it is the token it shares.'
        )}
      </>,
      <>
        Each agent has its own identity and its own five-minute tokens. The log names the agent that acted, and each has
        only the access it was granted.
      </>,
      <>
        triage-bot misbehaves again.
        {beat >= 1 ? (
          <>
            {' '}
            Suspend <b>triage-bot</b> and it gets no new tokens. Dana and deploy-bot keep working.
          </>
        ) : (
          ''
        )}
      </>,
    ][phase];

  return (
    <Animated title="Who is calling, and what stops it" seq={seq} phases={ID_PHASES} narrate={narrate} beatMs={1100}>
      <div
        className="bq-lanes gf-id"
        role="img"
        aria-label="Animated diagram: a person and two agents make calls. On a shared personal token every call is logged as the person, and revoking the token stops all three. With their own identities each agent is logged by name, and suspending one agent stops only that agent."
      >
        {CALLERS.map((c, i) => {
          const off = stopped(i);
          const key = !own && c.kind === 'agent' ? 'token: dana@acme' : `token: ${c.name}`;
          const loggedAs = own ? c.name : 'dana@acme';
          return (
            <div key={c.name} className={`bq-lane${off ? ' stopped' : ''}`}>
              <div className="who">
                {c.name}
                <small>
                  {c.kind} · {key}
                </small>
              </div>
              <div className="track" aria-hidden="true">
                <span className="dot">call</span>
                <span className="dot">call</span>
              </div>
              <div className="st">
                {off ? (
                  <span className="pw-tag pw-no">{own ? 'suspended' : 'revoked'}</span>
                ) : (
                  <span className={`pw-tag ${own ? 'pw-ok' : 'pw-off'}`}>log: {loggedAs}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Animated>
  );
}

// ─── 3. /mcp-audit-logs: the two logs filling in ────────────────────────────

const LOG_PHASES: Phase[] = [
  { act: 'Activity', label: 'calls', beats: 3 },
  { act: 'Audit Logs', label: 'access change', beats: 2 },
  { act: 'Activity', label: 'refused', beats: 2 },
  { act: 'Your SIEM', label: 'export', beats: 2 },
];

/** Position of each entry in the whole run; an entry shows once the run has passed it. */
const step = (phase: number, beat: number) => [0, 3, 5, 7][phase] + beat;

export function AuditLogFeed() {
  const seq = useSequence(LOG_PHASES, { beatMs: 1000 });
  const now = step(seq.phase, seq.beat);
  const shown = (at: number) => (now >= at ? '' : ' gone');

  const narrate = (phase: number, beat: number) =>
    [
      <>
        Calls go through the gateway. Each lands in <b>Activity</b> with the tool, server, caller, result, status code
        and latency.{beat >= 2 && ' People and agents are named separately.'}
      </>,
      <>
        An admin denies <code>db.deleteRows</code> for ci-agent.
        {beat >= 1 && (
          <>
            {' '}
            The change is recorded in <b>Audit Logs</b>: what changed, who did it, and the result.
          </>
        )}
      </>,
      <>
        ci-agent tries the delete anyway.
        {beat >= 1 && (
          <>
            {' '}
            The refusal is recorded as a failed <b>403</b>, with the agent&rsquo;s name and the tool it tried.
          </>
        )}
      </>,
      <>
        On Business and up, a webhook delivers both logs to your SIEM.
        {beat >= 1 && (
          <>
            {' '}
            Each delivery is signed in the <code>X-Statio-Signature</code> header, so your side can check it came from
            Statio.
          </>
        )}
      </>,
    ][phase];

  return (
    <Animated title="Two logs, filling in" seq={seq} phases={LOG_PHASES} narrate={narrate} beatMs={1000}>
      <div
        className="bq-cols"
        role="img"
        aria-label="Animated diagram: tool calls fill the Activity log, an admin's access change lands in Audit Logs, the refused call that follows is logged as a failed 403, and both logs are delivered to a SIEM by a signed webhook."
      >
        <div>
          <div className="bw-k">Activity</div>
          <div className="bw-log">
            <div className={`l bq-fade${shown(0)}`}>
              <span className="pw-tag pw-ok">allowed</span>
              <span>db.selectRows</span>
              <span className="dim">postgres · agent ci-agent · 200 · 42ms</span>
            </div>
            <div className={`l bq-fade${shown(1)}`}>
              <span className="pw-tag pw-ok">allowed</span>
              <span>issues.create</span>
              <span className="dim">github · user alex@acme · 200 · 61ms</span>
            </div>
            <div className={`l bq-fade${shown(2)}`}>
              <span className="pw-tag pw-ok">allowed</span>
              <span>db.selectRows</span>
              <span className="dim">postgres · agent ci-agent · 200 · 38ms</span>
            </div>
            <div className={`l bq-fade${shown(6)}`}>
              <span className="pw-tag pw-no">failed</span>
              <span>db.deleteRows</span>
              <span className="dim">postgres · agent ci-agent · 403 · 3ms</span>
              <span className="err">{DENY_MSG('db.deleteRows')}</span>
            </div>
          </div>
        </div>
        <div>
          <div className="bw-k">Audit Logs</div>
          <div className="bw-log">
            <div className={`l bq-fade${shown(4)}`}>
              <span className="pw-tag pw-ok">success</span>
              <span>alex@acme denied db.deleteRows for agent ci-agent</span>
            </div>
          </div>
          <div className="bw-k" style={{ marginTop: 14 }}>
            Webhook to your SIEM
          </div>
          <div className={`bw-log bq-fade${shown(8)}`}>
            <div className="l">
              <span className="dim">POST</span>
              <span>https://siem.acme.dev/statio</span>
            </div>
            <div className="l">
              <span className="dim">X-Statio-Signature:</span>
              <span>v1=9f2c…e41a</span>
            </div>
          </div>
        </div>
      </div>
    </Animated>
  );
}
